/**
 * Google Analytics (gtag.js) Integration
 * Gestisce il tracciamento Google Analytics rispettando il consenso dei cookie
 */

interface GtagFunction {
  (command: 'js', date: Date): void;
  (command: 'config', targetId: string, config?: Record<string, unknown>): void;
  (command: 'event', eventName: string, params?: Record<string, unknown>): void;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
  }
}

const GA_MEASUREMENT_ID = 'G-KTSE8BHE20';

/**
 * Verifica se il consenso ai cookie di tracciamento è stato dato
 */
function hasTrackingConsent(): boolean {
  const consent = sessionStorage.getItem('cookieConsent');
  return consent === 'accepted';
}

/**
 * Inizializza Google Analytics
 */
export function initGoogleAnalytics(): void {
  if (!hasTrackingConsent()) {
    return;
  }

  // Verifica se gtag è già stato caricato
  if (window.gtag) {
    return;
  }

  // Inizializza dataLayer
  window.dataLayer = window.dataLayer || [];

  // Definisce la funzione gtag
  function gtag(...args: unknown[]): void {
    if (window.dataLayer) {
      window.dataLayer.push(args);
    }
  }

  window.gtag = gtag as GtagFunction;

  // Carica lo script di Google Analytics
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);

  // Inizializza Google Analytics
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID);
}

/**
 * Traccia un evento personalizzato
 */
export function trackGAEvent(eventName: string, params?: Record<string, unknown>): void {
  if (!hasTrackingConsent() || !window.gtag) {
    return;
  }

  window.gtag('event', eventName, params);
}

/**
 * Traccia un click su un bottone
 */
export function trackGAButtonClick(buttonName: string, additionalParams?: Record<string, unknown>): void {
  trackGAEvent('button_click', {
    button_name: buttonName,
    ...additionalParams,
  });
}

/**
 * Traccia una page view
 */
export function trackGAPageView(path?: string): void {
  if (!hasTrackingConsent() || !window.gtag) {
    return;
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path || window.location.pathname,
  });
}

/**
 * Traccia una prenotazione
 */
export function trackGAReservation(params?: Record<string, unknown>): void {
  trackGAEvent('reservation', params);
}

/**
 * Traccia una chiamata telefonica
 */
export function trackGAPhoneCall(): void {
  trackGAEvent('contact', {
    method: 'phone',
  });
}

/**
 * Traccia la visualizzazione del menu
 */
export function trackGAMenuView(menuType?: string): void {
  trackGAEvent('view_item', {
    item_category: 'menu',
    item_name: menuType || 'menu',
  });
}

